import { addHours } from "date-fns";
import {
  SECONDS_PER_DAY,
  SECONDS_PER_HOUR,
  SECONDS_PER_MINUTE,
  SECONDS_PER_YEAR,
} from "./time.constants";

interface SecondsToHumanReadableTypes {
  seconds: number | string;
  tz?: number;
  isTimestamp?: boolean;
  /** With isTimestamp, render time-of-day only (HH:mm:ss) without the date. */
  timeOnly?: boolean;
  round?: boolean;
  minMax?: [number, number];
}

export function secondsToHumanReadable(
  arg: number | string | SecondsToHumanReadableTypes
): string {
  let seconds: number | string;
  let tz: number | undefined;
  let isTimestamp: boolean | undefined = false;
  let timeOnly: boolean | undefined = false;
  let round: boolean | undefined = false;
  let minMax: [number, number] | undefined;

  if (!arg) {
    return "";
  }

  if (typeof arg === "object") {
    ({
      seconds,
      tz,
      isTimestamp = false,
      timeOnly = false,
      round = false,
      minMax,
    } = arg);
  } else {
    seconds = arg;
  }

  seconds = Number(seconds);
  if (Number.isNaN(seconds)) {
    return "";
  }

  // round the seconds if they are more than 60, since milliseconds are irrelevant
  if (seconds > 60) {
    seconds = Math.round(seconds);
  }

  if (isTimestamp) {
    let date = new Date(seconds * 1000);
    if (tz) date = addHours(date, tz);
    const timestamp = formatTimestampInUTC(date);
    // "yyyy-MM-dd HH:mm:ss" -> "HH:mm:ss"
    return timeOnly ? timestamp.split(" ")[1] : timestamp;
  }

  let timeUnits = getTimeUnits(seconds);

  if (minMax) {
    timeUnits = getRoundedTimeUnits(timeUnits, minMax);
  }

  // do not call formatRounded if `minMax` is provided because
  // the value is probably a part of a series of numbers and shouldn't be rounded
  return round && !minMax ? formatRounded(timeUnits) : formatNonRounded(timeUnits);
}

/**
 * Formats a Date as "yyyy-MM-dd HH:mm:ss" using UTC, regardless of browser timezone.
 * The caller is responsible for applying any timezone offset (e.g. via addHours) before passing the date.
 */
export const formatTimestampInUTC = (date: Date): string => {
  return date
    .toLocaleString("en-CA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
      timeZone: "UTC",
    })
    .replace(",", "");
};

export type TimeUnits = {
  years: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

export const getTimeUnits = (seconds: number): TimeUnits => ({
  years: Math.floor(seconds / SECONDS_PER_YEAR),
  days: Math.floor((seconds % SECONDS_PER_YEAR) / SECONDS_PER_DAY),
  hours: Math.floor((seconds % SECONDS_PER_DAY) / SECONDS_PER_HOUR),
  minutes: Math.floor((seconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE),
  seconds: seconds % SECONDS_PER_MINUTE,
});

// round the time units relative to the max value.
// manual rounding rules according to the max value
export const getRoundedTimeUnits = (
  timeUnits: TimeUnits,
  minMax: [number, number]
) => {
  const deltaTimeUnits = getTimeUnits(minMax[1] - minMax[0]);

  if (deltaTimeUnits.minutes >= 3) {
    timeUnits.seconds = Math.round(timeUnits.seconds);
    timeUnits.minutes += timeUnits.seconds >= 60 ? 1 : 0;
  }

  if (deltaTimeUnits.minutes > 10) {
    timeUnits.minutes += timeUnits.seconds > 30 ? 1 : 0;
    timeUnits.seconds = 0;
  }

  if (deltaTimeUnits.hours >= 1) {
    if (deltaTimeUnits.hours >= 10) {
      timeUnits.hours += timeUnits.minutes > 30 ? 1 : 0;
      timeUnits.minutes = 0;
    }
    timeUnits.seconds = 0;
  }

  if (deltaTimeUnits.days >= 3) {
    timeUnits.hours = Math.round(timeUnits.hours);
    timeUnits.days += timeUnits.hours >= 24 ? 1 : 0;
    timeUnits.hours = 0;
  }

  return timeUnits;
};

export function formatRounded(timeUnits: TimeUnits): string {
  let { years, days, hours, minutes, seconds } = timeUnits;

  if (years > 0) {
    return `${years} year${years > 1 ? "s" : ""} ${days ? `${days}d` : ""}`.trim();
  }

  if (days >= 10) {
    return `${days}d`;
  }

  if (days > 0) {
    return `${days}d ${hours > 0 ? `${hours}h` : ""}`.trim();
  }

  if (hours > 0) {
    const roundedMinutes = Math.round(minutes + seconds / SECONDS_PER_MINUTE);
    return `${hours}h${roundedMinutes ? ` ${roundedMinutes}m` : ""}`.trim();
  }

  if (minutes > 30) {
    return `${minutes}m`;
  }

  if (minutes > 0) {
    // round up the seconds if minutes are 10 or more and seconds are 45 or more
    if (minutes >= 10 && seconds > 30) {
      return `${minutes + 1}m`;
    }

    // Special case for minutes <= 10
    if (minutes < 10) {
      const roundedSeconds = Math.round(seconds);
      minutes = roundedSeconds >= 60 ? minutes + 1 : minutes;

      return `${minutes}m ${roundedSeconds ? `${roundedSeconds}s` : ""}`.trim();
    }

    return `${minutes}m`;
  }

  // Round up to 1m if seconds are very close to 60
  if (seconds >= 59.5) {
    return "1m";
  }

  // Round seconds to 1 decimal place if they are 3 or more
  if (seconds >= 3) {
    return Math.round(seconds) + "s";
  }

  return getFixedSeconds(seconds);
}

export function formatNonRounded(units: Record<string, number>): string {
  const { years, days, hours, minutes } = units;
  let seconds = units.seconds;

  if (years) {
    return `${years} year${years > 1 ? "s" : ""} ${days ? `${days}d` : ""}`.trim();
  }

  if (days >= 10) {
    return `${days}d`;
  }

  if (days) {
    return `${days}d ${hours ? `${hours}h` : ""}`.trim();
  }

  if (Object.values(units).every((v) => v === 0)) {
    return "-";
  }

  if (minutes && seconds) {
    seconds = +seconds.toFixed(1);
  }

  let result = "";

  if (hours > 0) result += `${hours}h `;
  if (minutes > 0) result += `${minutes}m `;
  if (seconds > 0 || result === "") result += getFixedSeconds(seconds);

  return result.trim();
}

export const getFixedSeconds = (seconds: number) =>
  `${seconds.toFixed(1)}s`.replace(".0s", "s");
